import React, { useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";

import { OrganizationEndpoints } from "@generated/organization";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { TagChips } from "src/shell/components/TagChips";
import {
  getCardStyles,
  getFocusRingSx,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

interface Organization {
  id: string | number;
  name: string;
  alias: string;
  description?: string;
  tags?: Record<string, string>;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

interface GeneralSettingsCardProps {
  organization: Organization;
  onSaved?: () => void;
}

const GeneralSettingsCard: React.FC<GeneralSettingsCardProps> = ({
  organization,
  onSaved,
}) => {
  const theme = useTheme();
  const [name, setName] = useState(organization.name);
  const [description, setDescription] = useState(
    organization.description ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await axios.patch(
        `${apiBaseUrls.BACKEND}${OrganizationEndpoints.UPDATE_ORGANIZATION.replace(":id", encodeURIComponent(organization.alias))}`,
        { name, description: description || undefined },
        {
          headers: {
            Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
            "Content-Type": "application/json",
          },
        },
      );
      setSaved(true);
      onSaved?.();
    } catch {
      setError("Failed to save. Please try again or contact support.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card sx={getCardStyles(theme)}>
      <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>
        General Settings
      </Typography>

      <Stack spacing={2.5}>
        {/* Name */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Name
          </Typography>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
            disabled={saving}
            sx={getFocusRingSx(theme)}
          />
        </Box>

        {/* Alias (read-only) */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Alias (read-only)
          </Typography>
          <TextField
            fullWidth
            value={organization.alias}
            disabled
            sx={{
              ...getFocusRingSx(theme),
              "& .MuiOutlinedInput-root": { opacity: 0.6 },
            }}
            InputProps={{
              endAdornment: (
                <Chip
                  label="LOCKED"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    bgcolor: "divider",
                    color: "text.secondary",
                  }}
                />
              ),
            }}
          />
        </Box>

        {/* Description */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
          >
            Description
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setSaved(false);
            }}
            disabled={saving}
            sx={getFocusRingSx(theme)}
          />
        </Box>

        {/* Tags (read-only display) */}
        {organization.tags && Object.keys(organization.tags).length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
            >
              Tags
            </Typography>
            <TagChips tags={organization.tags} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ borderRadius: 1 }}>
            {error}
          </Alert>
        )}
        {saved && !error && (
          <Alert severity="success" sx={{ borderRadius: 1 }}>
            Changes saved successfully.
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSave()}
            disabled={saving || !name.trim()}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              "&:focusVisible": {
                boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}33`,
              },
              "&:active": { transform: "scale(0.98)" },
              transition: "all 150ms ease-out",
            }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </Box>
      </Stack>
    </Card>
  );
};

export default GeneralSettingsCard;
